import React, { useState } from "react";
import { toast } from "react-toastify";
import Search from "../../components/form/Search";
import Button from "../../components/form/Button";
import Table from "../../components/elements/table/Table";
import { setTitle } from "../../helpers/MetaTag";
import { handleFilterChange } from "../../helpers";
import DeleteModal from "../../components/common/DeleteModal";

import useFetch from "../../hooks/useFetch";
import { useDebounce } from "../../hooks/useDebounce";
import LimitSelect from "../../components/elements/LimitSelect";
import useMutation from "../../hooks/useMutation";
import {
  transactionDelete,
  transactionList,
  transactionStatusChange,
} from "../../services/transactionService";
import dayjs from "dayjs";
import ManageDonationModal from "../../components/sections/admin/donation/modals/ManageDonationModal";
import { useNavigate } from "react-router-dom";
import { useGetPermission } from "../../hooks/useGetPermission";
import RecurringDonationModal from "../../components/sections/admin/donation/modals/RecurringDonationModal";
import { pdf } from "@react-pdf/renderer"; // your PDF document
import fileSaver from "file-saver"; // to trigger download
import DonationReciptPdf from "../../pdfs/DonationReciptPdf";

const Donation = () => {
  setTitle("Vpeters Admin | Donation");
  const navigate = useNavigate();
  const { permission, status } = useGetPermission("donations");
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [recurringModalOpen, setRecurringModalOpen] = useState({
    isOpen: false,
    data: null,
  });
  const [transactionModalOpen, setTransactionModalOpen] = useState({
    isOpen: false,
    data: null,
  });

  const [keyword, setKeyword] = useState("");
  const [refNumber, setRefNumber] = useState("");
  const search = useDebounce(keyword, 300);
  const refNoSearch = useDebounce(refNumber, 600);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });
  const { data, error, isLoading, updateData, isFetching, refetch } = useFetch({
    dependencies: ["donation", filters, search, refNoSearch, permission?.list],
    fetchFunction: async () => {
      const res = await transactionList({
        ...filters,
        categoryType: "donation",
        ...(search && { keyword: search }),
        ...(refNoSearch && { referenceNumber: refNoSearch }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!permission?.list && status === "ok",
  });

  //   donation status change handler =================>
  const { mutate: statusChangeHandler, isPending: statusChangeInPending } =
    useMutation({
      mutationFn: async ({ id, status }) => {
        const res = await transactionStatusChange({
          ids: id,
          status,
        });
        return { res, payload: { id, status } };
      },
      onSuccess: ({ res, payload }) => {
        if (res?.status !== 200) {
          updateData((pre) => {
            pre.data = (pre?.data || []).map((elm) =>
              elm?._id === payload?.id
                ? { ...elm, status: payload?.status === 1 ? 2 : 1 }
                : elm
            );
            return { ...pre };
          });
        }
      },
      onError: (error) => {
        console.log(error);
      },
    });

  const handleDownloadReceipt = async (item) => {
    const fileName = `Receipt-${item?.invoiceId || "N/A"}.pdf`;
    const blob = await pdf(<DonationReciptPdf data={item} />).toBlob();
    fileSaver.saveAs(blob, fileName);
  };

  const tableHeadData = [
    {
      _id: 2,
      name: "Donor Name",
      align: "left",
    },
    {
      _id: 3,
      name: "Email",
      align: "left",
    },
    {
      _id: 4,
      name: "Amount",
      align: "left",
    },
    {
      _id: 5,
      name: "Category",
      align: "left",
    },
    {
      _id: 6,
      name: "Type",
      align: "left",
    },
    {
      _id: 7,
      name: "Organization Name",
      align: "left",
    },
    {
      _id: 8,
      name: "Reference#",
      align: "left",
    },
    {
      _id: 9,
      name: "Date",
      align: "left",
    },
    {
      _id: 10,
      name: "Payment Method",
      align: "left",
    },
    {
      _id: 11,
      name: "Status",
      align: "left",
    },
    {
      _id: 12,
      name: "Auto Email Sent",
      align: "left",
    },
    {
      _id: 13,
      name: "Action",
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.name || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.email || "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.amount || "-",
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.donationFrequency || "-",
        },
        {
          _id: 6,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.donationType || "-",
        },
        {
          _id: 7,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.organizationName || "-",
        },
        {
          _id: 8,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.invoiceId || "-",
        },
        {
          _id: 9,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.date ? dayjs(item?.date).format("DD MMM, YYYY") : "-",
        },
        {
          _id: 10,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.paymentMethod || "-",
        },
        {
          _id: 11,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.paymentStatus || "-",
        },
        {
          _id: 12,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.isEmail ? (item?.isEmail === true ? "yes" : "no") : "-",
        },
        {
          _id: 13,
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            ...(permission?.edit && status === "ok"
              ? [
                  {
                    _id: 1,
                    name: "Edit",
                    icon: "fa-regular fa-pen-to-square",
                    onClick: () => {
                      setModalOpen({ isOpen: true, data: item });
                    },
                  },
                ]
              : []),
            {
              _id: 2,
              name: "Download Receipt",
              icon: "fa-regular fa-download",
              onClick: () => {
                handleDownloadReceipt(item);
                // navigate(`/admin/donations/pdf`, {
                //   state: {
                //     data: item,
                //     type: "DonationReciptPdf",
                //     fileName: `Receipt-${item?.invoiceId || "N/A"}.pdf`,
                //   },
                // });
              },
            },
            ...(item?.donationFrequency === "monthly"
              ? [
                  {
                    _id: 3,
                    name: "Add Transaction",
                    icon: "fa-regular fa-hand-holding-circle-dollar",
                    onClick: () => {
                      setRecurringModalOpen({ isOpen: true, data: item });
                    },
                  },
                ]
              : []),
            ...(permission?.delete && status === "ok"
              ? [
                  {
                    _id: 4,
                    name: "Delete",
                    icon: "fa-regular fa-trash-can",
                    standout: true,
                    onClick: () => {
                      setDeleteModal({
                        isOpen: true,
                        data: item,
                      });
                    },
                  },
                ]
              : []),
          ],
        },
      ],
    };
  });

  // add or edit donation =================>
  function donationManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          updateData((pre) => ({
            ...pre,
            data: [res?.data, ...(pre?.data || [])],
          }));
          refetch();
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        updateData((pre) => ({
          ...pre,
          data: pre?.data?.map((item) =>
            item?._id === res?.data?._id ? res?.data : item
          ),
        }));
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //  donation delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await transactionDelete({
        ids: Array.isArray(payload) ? payload : [payload],
        categoryType: "donation",
      });
      return { res, payload: Array.isArray(payload) ? payload : [payload] };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        updateData((pre) => {
          pre.data = (pre?.data || [])?.filter(
            (item) => !payload?.includes(item?._id)
          );
          return { ...pre };
        });
        if (data?.metadata?.totalDocs > filters?.limit) {
          refetch();
        }
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-bold text-center text-lg xl:text-3xl text-chestnut-rose">
          Donations
        </div>
        <div className="flex items-center gap-3">
          <div className="w-36">
            <LimitSelect
              value={filters?.limit}
              allValue={data?.metadata?.totalDocs}
              onChange={(val) => handleFilterChange("limit", val, setFilters)}
            />
          </div>
          <div className="w-72">
            <Search
              placeholder={"Search here"}
              search={(val) => {
                setKeyword(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
          <div className="w-72">
            <Search
              placeholder={"Ref# search here"}
              search={(val) => {
                setRefNumber(val);
                handleFilterChange("page", 1, setFilters);
              }}
            />
          </div>
          {permission?.add && status === "ok" && (
            <div className="relative">
              <Button
                buttonClasses={
                  "!bg-transparent border border-transparent !h-10 hover:!bg-chestnut-rose/10 hover:!border-chestnut-rose/10"
                }
                buttonLabel={"Create New"}
                buttonLabelClasses={"font-medium !text-sm text-chestnut-rose"}
                buttonFunction={() =>
                  setModalOpen({ isOpen: true, data: null })
                }
              />
            </div>
          )}
          <div className="ml-auto space-x-3">
            <span className="text-chestnut-rose font-CanelaDeck">
              Total : {data?.metadata?.totalDocs || 0}
            </span>
            <span className="text-chestnut-rose font-CanelaDeck">
              Pending : {data?.metadata?.extraCount?.pendingDonation || 0}
            </span>
          </div>
        </div>
      </div>
      {status === "ok" &&
        (permission?.list ? (
          <Table
            tableHeadData={tableHeadData}
            tableData={tableData}
            isLoder={isLoading}
            pageCount={data?.metadata?.totalPages}
            currentPage={filters?.page}
            onPageChange={(val) => {
              handleFilterChange("page", val, setFilters);
              setSelectedItems([]);
            }}
            isFetching={isFetching}
            // isOrderingDisabled={true}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            tableActionDropdownContainer={"!w-48"}
            handleOrdering={(newData, oldData) => {
              // donationOrdering(newData, oldData);
            }}
            originalData={[...(data?.data || [])]}
            setOriginalData={(data) =>
              updateData((pre) => {
                pre.data = data;
                return { ...pre };
              })
            }
          />
        ) : (
          <div className="font-semibold text-xl text-center h-60 flex items-center justify-center text-shades-07">
            You don't have permission to view this list.
          </div>
        ))}
      <ManageDonationModal
        open={modalOpen?.isOpen}
        data={modalOpen?.data}
        close={() => setModalOpen({ isOpen: false, data: null })}
        getResponse={donationManageResponse}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
      <RecurringDonationModal
        open={recurringModalOpen?.isOpen}
        data={recurringModalOpen?.data}
        close={() => setRecurringModalOpen({ isOpen: false, data: null })}
      />
    </div>
  );
};

export default Donation;
